const MAX_SIZE = 5 * 1024 * 1024;
const MAX_XML_SIZE = 20 * 1024 * 1024;
const elements = (node, name) => Array.from(node.getElementsByTagNameNS('*', name));
const normalize = value => value.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export async function openContactsTemplate(file) {
  const fail = () => { throw new Error('contacts_template_invalid'); };
  if (!/\.xlsx$/i.test(file.name) || file.size > MAX_SIZE) fail();
  const bytes = new Uint8Array(await file.arrayBuffer());
  const view = new DataView(bytes.buffer);
  const entries = new Map();
  let end = bytes.length - 22;
  while (end >= Math.max(0, bytes.length - 65557)) {
    if (view.getUint32(end, true) === 0x06054b50 && end + 22 + view.getUint16(end + 20, true) === bytes.length) break;
    end -= 1;
  }
  if (end < 0 || end < bytes.length - 65557) fail();
  if (view.getUint16(end + 4, true) || view.getUint16(end + 6, true)) fail();
  const count = view.getUint16(end + 10, true);
  let offset = view.getUint32(end + 16, true);
  for (let i = 0; i < count; i += 1) {
    if (offset + 46 > end || view.getUint32(offset, true) !== 0x02014b50) fail();
    const nameLength = view.getUint16(offset + 28, true);
    const next = offset + 46 + nameLength + view.getUint16(offset + 30, true) + view.getUint16(offset + 32, true);
    if (next > end) fail();
    const name = new TextDecoder().decode(bytes.subarray(offset + 46, offset + 46 + nameLength));
    if (entries.has(name)) fail();
    entries.set(name, {
      flags: view.getUint16(offset + 8, true),
      method: view.getUint16(offset + 10, true),
      size: view.getUint32(offset + 20, true),
      expanded: view.getUint32(offset + 24, true),
      offset: view.getUint32(offset + 42, true),
    });
    offset = next;
  }
  let total = 0;
  const readXml = async name => {
    const entry = entries.get(name);
    if (!entry || entry.flags & 1 || entry.expanded > MAX_XML_SIZE) fail();
    const start = entry.offset;
    if (start + 30 > end || view.getUint32(start, true) !== 0x04034b50) fail();
    const dataStart = start + 30 + view.getUint16(start + 26, true) + view.getUint16(start + 28, true);
    if (dataStart + entry.size > end) fail();
    const compressed = bytes.subarray(dataStart, dataStart + entry.size);
    let output;
    if (entry.method === 0) {
      output = compressed;
      total += output.length;
    } else if (entry.method === 8) {
      let decompressor;
      try {
        decompressor = new DecompressionStream('deflate-raw');
      } catch (error) {
        throw new Error('contacts_template_browser');
      }
      const reader = new Blob([compressed]).stream().pipeThrough(decompressor).getReader();
      const chunks = [];
      let length = 0;
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          length += value.length;
          total += value.length;
          if (total > MAX_XML_SIZE) fail();
          chunks.push(value);
        }
      } finally {
        await reader.cancel();
      }
      output = new Uint8Array(length);
      let position = 0;
      chunks.forEach(chunk => { output.set(chunk, position); position += chunk.length; });
    } else fail();
    if (total > MAX_XML_SIZE || output.length !== entry.expanded) fail();
    const xml = new DOMParser().parseFromString(new TextDecoder().decode(output), 'application/xml');
    if (elements(xml, 'parsererror').length) fail();
    return xml;
  };
  return { readXml, hasEntry: name => entries.has(name) };
}

export default async function readContactsTemplate(file) {
  const fail = () => { throw new Error('contacts_template_invalid'); };
  const { readXml, hasEntry } = await openContactsTemplate(file);
  const workbook = await readXml('xl/workbook.xml');
  const sheet = elements(workbook, 'sheet')[0];
  if (!sheet) fail();
  const relationships = await readXml('xl/_rels/workbook.xml.rels');
  const relationId = sheet.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'id');
  const relation = elements(relationships, 'Relationship').find(item => item.getAttribute('Id') === relationId);
  if (!relation || relation.getAttribute('TargetMode') === 'External') fail();
  const target = relation.getAttribute('Target');
  const path = new URL(target, 'https://template.local/xl/workbook.xml');
  if (path.origin !== 'https://template.local') fail();
  const strings = hasEntry('xl/sharedStrings.xml')
    ? elements(await readXml('xl/sharedStrings.xml'), 'si').map(item => elements(item, 't').map(text => text.textContent).join(''))
    : [];
  const worksheet = await readXml(path.pathname.slice(1));
  const rows = elements(worksheet, 'row').map(row => {
    const cells = new Map();
    elements(row, 'c').forEach(cell => {
      if (elements(cell, 'f').length || cell.getAttribute('t') === 'e') fail();
      const reference = /^([A-Z]+)[1-9]\d*$/.exec(cell.getAttribute('r') || '');
      if (!reference) fail();
      const type = cell.getAttribute('t');
      let value = elements(cell, 'v')[0]?.textContent || '';
      if (type === 's') {
        if (!/^\d+$/.test(value) || strings[Number(value)] === undefined) fail();
        value = strings[Number(value)];
      }
      if (type === 'inlineStr') value = elements(cell, 't').map(text => text.textContent).join('');
      cells.set(reference[1], value.trim());
    });
    return cells;
  }).filter(row => Array.from(row.values()).some(Boolean));
  const header = rows.shift();
  const fields = {
    section: ['seccion', 'section', 'secao'],
    phone_number: ['telefono', 'phone_number', 'phone', 'telefone'],
    name: ['nombre', 'name', 'nome'],
    description: ['descripcion', 'description', 'descricao'],
  };
  const columns = Object.entries(fields).map(([field, aliases]) => {
    const matches = header ? Array.from(header).filter(([, value]) => aliases.includes(normalize(value))) : [];
    if (matches.length !== 1) fail();
    return [field, matches[0][0]];
  });
  if (!rows.length) throw new Error('contacts_template_empty');
  return rows.map(row => Object.fromEntries(columns.map(([field, column]) => [field, row.get(column) || ''])));
}
